import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * GlobalSyncStatus — small header pill that shows Base44 + Stripe auto-sync
 * health on every page. Pulses green when fresh (<6min old), yellow warn
 * when stale (>10min), red when a loop stopped. Clicking opens a popover
 * with the exact last-synced timestamps and per-integration "sync now"
 * buttons so the operator can force-refresh without leaving the current page.
 */
export default function GlobalSyncStatus({ variant = "desktop" }) {
  const [base, setBase] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null); // "base44" | "stripe" | null

  const load = async () => {
    try {
      const [b, s] = await Promise.all([
        api.get("/orders/sync/status").catch(() => ({ data: null })),
        api.get("/stripe/sync/status").catch(() => ({ data: null })),
      ]);
      setBase(b.data);
      setStripe(s.data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const status = classify(base, stripe);

  const syncNow = async (which) => {
    setBusy(which);
    try {
      if (which === "base44") {
        const { data } = await api.get("/orders/sync/base44");
        toast.success(`Base44 synced · ${data.synced || 0} orders`);
      } else {
        const { data } = await api.post("/stripe/sync-charges");
        toast.success(`Stripe synced · ${data.synced || 0} charges`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(null);
    }
  };

  const tone =
    status.level === "ok"
      ? "bg-primary/10 text-primary border-primary/30"
      : status.level === "warn"
      ? "bg-[#E5A93D]/15 text-[#7c5a14] border-[#E5A93D]/40"
      : "bg-[#D66853]/15 text-[#a44231] border-[#D66853]/40";

  const Icon = status.level === "ok" ? CheckCircle2 : status.level === "warn" ? AlertTriangle : AlertTriangle;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`global-sync-status-${variant}`}
        className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${tone} hover:opacity-90 transition-opacity`}
      >
        <span className="relative flex h-2 w-2">
          {status.level === "ok" && (
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-50" />
          )}
          <span className="relative inline-flex rounded-full h-2 w-2 bg-current" />
        </span>
        <span className="font-medium">{status.label}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 top-full mt-2 w-80 bg-white border border-border rounded-lg shadow-lg z-40 p-4"
            data-testid="global-sync-popover"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="label-eyebrow">Live sync</div>
              <Icon className={`w-4 h-4 ${status.level === "ok" ? "text-primary" : status.level === "warn" ? "text-[#7c5a14]" : "text-[#a44231]"}`} />
            </div>

            <SyncRow
              label="Base44 orders"
              state={base}
              busy={busy === "base44"}
              onSync={() => syncNow("base44")}
            />
            <div className="h-px bg-border/60 my-3" />
            <SyncRow
              label="Stripe payments"
              state={stripe}
              busy={busy === "stripe"}
              onSync={() => syncNow("stripe")}
            />

            <div className="mt-3 pt-3 border-t border-border/60 text-[11px] text-muted-foreground">
              Both integrations refresh every 5 min automatically. Force-sync anytime with the
              buttons above.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SyncRow({ label, state, busy, onSync }) {
  const configured = state?.configured;
  const lastAt = state?.last_synced_at;
  const nextIn = state?.next_sync_in_seconds;
  const running = state?.auto_sync_running;
  const rel = lastAt ? relTime(lastAt) : "never";
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {!configured ? (
          <div className="text-xs text-muted-foreground mt-0.5">Not configured</div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground mt-0.5">
              Last synced <span className="text-foreground">{rel}</span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {running
                ? nextIn != null
                  ? `Next auto-sync in ${Math.max(nextIn, 0)}s`
                  : "Auto-sync active"
                : "Auto-sync stopped"}
            </div>
          </>
        )}
      </div>
      {configured && (
        <button
          type="button"
          onClick={onSync}
          disabled={busy}
          className="shrink-0 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border border-border hover:bg-secondary disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
          Sync now
        </button>
      )}
    </div>
  );
}

function classify(base, stripe) {
  const parts = [];
  if (base?.configured) parts.push(base);
  if (stripe?.configured) parts.push(stripe);
  if (parts.length === 0) return { level: "warn", label: "Not configured" };

  let stalest = 0;
  let anyStopped = false;
  for (const p of parts) {
    if (!p.auto_sync_running) anyStopped = true;
    if (p.last_synced_at) {
      const age = (Date.now() - new Date(p.last_synced_at).getTime()) / 1000;
      if (age > stalest) stalest = age;
    } else {
      stalest = Infinity;
    }
  }
  if (anyStopped) return { level: "err", label: "Sync stopped" };
  if (stalest > 900) return { level: "err", label: "Stale" };
  if (stalest > 480) return { level: "warn", label: "Delayed" };
  return { level: "ok", label: "Syncing" };
}

function relTime(iso) {
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s ago`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m ago`;
}
